// Database types for RealityOperator

export interface Room {
  id: string;
  name: string;
  location?: string;
  configuration_id?: string;
  discord_lobby_channel_id?: string;
  created_at: string;
  updated_at: string;
}

// Per-location customer queue. Returned by booking.fn_get_queue. Source of
// truth for both the dashboard NextUpWidget and the second-screen
// QueueDisplay. Post per-person migration: one entry per individual.
// Queue v2 (20260904155412_booking_queue_v2_rpcs): play language, parked
// ("even weg") and an ETA per entry; parked rows stay IN PLACE, so the head
// of the queue is the first non-parked row.
export interface QueueEntry {
  queue_entry_id: string;
  departure_id: string;
  booking_id: string;
  departure_number: string;
  /** 1-based index within the party. Siblings of one departure share
   *  departure_id and differ only by person_index. */
  person_index: number;
  /** 1-based visual position in the location's queue. */
  position: number;
  /** Underlying fractional position. */
  position_float: number;
  entered_at: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  /** Total party_size for this departure (display only; same across siblings). */
  party_size: number;
  /** Aggregated as "Egypt + Odyssey" — matches fn_checkin_booking output. */
  experience_names: string | null;
  /** Per-pick breakdown of the booked experiences. Lets the dispatch pop-up
   *  resolve the BOOKED experience to a launchable release by experience_id. */
  experiences: QueuePick[] | null;
  /** Earliest slot start, pre-cached on departures.departure_start. */
  departure_start: string;
  room_id: string | null;
  room_name: string | null;
  /** Effective play language, LOWERCASE 2-letter code (departures.play_language,
   *  else nl for a Dutch booking locale, else en). Map through
   *  agentLanguageCode() before handing it to the agent / LanguageFlag. */
  play_language: string;
  /** true once an operator confirmed/corrected the language (check-in or dispatch). */
  play_language_confirmed: boolean;
  /** "Even weg": keeps the spot, dispatch skips the row. */
  parked: boolean;
  /** The operator has asked this visitor to come to the desk (fn_call_entry). */
  called: boolean;
  parked_at: string | null;
  /** Non-parked people in front of this row in the visible order. */
  people_ahead: number;
  /** Estimated wait in minutes (rounded up to 5); 0 for the head, null when parked. */
  eta_minutes: number | null;
}

export type QueueOrderPolicy = 'slot_first' | 'position_only';

/** One experience pick within a departure (booking.departure_experiences). */
export interface QueuePick {
  experience_id: string;
  name: string;
  party_size: number;
  pick_index: number;
}

/** A queue row that was started today (booking.fn_get_started_today), with
 *  the PC it runs on. Drives the PC tile's "who plays here" line and the
 *  restart / move / requeue recovery actions. Newest event first. */
export interface StartedEntry {
  queue_entry_id: string;
  departure_id: string;
  booking_id: string;
  departure_number: string;
  person_index: number;
  position_float: number;
  customer_first_name: string | null;
  customer_last_name: string | null;
  party_size: number;
  experience_names: string | null;
  experiences: QueuePick[] | null;
  departure_start: string;
  play_language: string;
  play_language_confirmed: boolean;
  started_at: string;
  last_event_at: string;
  last_reason: string | null;
  last_operator_id: string | null;
  action_group_id: string | null;
  started_pc_id: string | null;
  started_pc_name: string | null;
}

/** One experience pick on a public queue row (booking.fn_get_queue_public).
 *  The public payload deliberately carries the MARKETED name in the visitor's
 *  play language plus the booking slug, so the display can show what the
 *  visitor recognises from the website and pick the matching logo. */
export interface QueuePublicExperience {
  experience_id: string;
  /** Marketed name in the play language ("Voyage of the Pharaohs"). */
  name: string;
  /** Internal/ops name ("Egypt"). Never shown to a visitor. */
  internal_name: string;
  /** experiences.booking_slug ("pharaohs", "odyssey", "maya"); null when unset. */
  slug: string | null;
  pick_index: number;
}

/** PII-minimal queue row for the public display (booking.fn_get_queue_public):
 *  surname only, numbered within a party, no first names or booking numbers. */
export interface QueuePublicEntry {
  queue_entry_id: string;
  position: number;
  /** Render verbatim: "Bakker 1" / "Bakker 2" for a party, "Bakker" solo,
   *  "Gast" when unknown. booking.fn_public_display_name owns this string —
   *  the display must never assemble a name of its own. */
  display_name: string;
  /** Stable per-departure key (md5 prefix) so siblings can be grouped. */
  party_key: string;
  person_index: number;
  party_size: number;
  /** Marketed names joined with " + " ("Voyage of the Pharaohs + Odyssey"). */
  experience_names: string | null;
  /** Per-pick detail, ordered by pick_index. Empty/absent on a legacy payload. */
  experiences: QueuePublicExperience[] | null;
  /** Lowercase 2-letter play language: 'nl' | 'en' | 'de'. */
  play_language: string | null;
  parked: boolean;
  /** The operator has asked this visitor to come to the desk (fn_call_entry). */
  called: boolean;
  is_next: boolean;
  /** null while parked; otherwise minutes rounded to 5. Counts the runs already
   *  in progress, so the head can legitimately read 10 while every PC is busy. */
  eta_minutes: number | null;
}

/** Today's arrived departure with no active queue entry — returned by
 *  booking.fn_get_arrived_not_queued for the operator's add-to-queue picker. */
export interface ArrivedDeparture {
  departure_id: string;
  departure_number: string;
  departure_start: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  party_size: number | null;
  experience_names: string | null;
}

export interface RoomVoiceChannel {
  id: string;
  room_id: string;
  discord_channel_id: string;
  name?: string;
  in_use: boolean;
  session_id?: string;
  created_at: string;
}

export interface Network {
  id: string;
  room_id: string;
  name: string;
  description?: string;
  created_at: string;
}

/** Why a PC is out of the lineup. A CATEGORY is required (the free-text note
 *  is not), so "buiten dienst" is never a mystery to the next shift and the
 *  desk can count how many machines are actually broken vs. merely parked.
 *  Mirrors the CHECK constraint in 20260916140000_pc_instances_out_of_service. */
export type OutOfServiceKind = 'repair' | 'investigating' | 'cleaning' | 'other';

export const OUT_OF_SERVICE_KINDS: { key: OutOfServiceKind; label: string; hint: string }[] = [
  { key: 'repair', label: 'Reparatie', hint: 'Kapot, moet gemaakt worden' },
  { key: 'investigating', label: 'Onderzoek', hint: 'We zoeken nog uit wat er mis is' },
  { key: 'cleaning', label: 'Schoonmaak', hint: 'Even uit de running, komt zo terug' },
  { key: 'other', label: 'Anders', hint: 'Iets anders — zet het in de notitie' },
];

export function outOfServiceLabel(kind?: OutOfServiceKind | null): string {
  return OUT_OF_SERVICE_KINDS.find((k) => k.key === kind)?.label ?? 'Buiten dienst';
}

/** One period a PC spent out of the lineup (operations.pc_out_of_service).
 *  Open while `ended_at` is null; at most one open row per PC. The closed rows
 *  are the downtime history — how often a machine goes down and for how long —
 *  which a flag on pc_instances alone could never answer. */
export interface PCOutOfService {
  id: string;
  pc_instance_id: string;
  kind: OutOfServiceKind;
  note: string | null;
  started_at: string;
  started_by: string | null;
  started_by_name: string | null;
  ended_at: string | null;
  ended_by: string | null;
  ended_by_name: string | null;
}

export interface PCInstance {
  id: string;
  network_id: string;
  mac_address: string;
  pc_name?: string;
  role: 'primary' | 'secondary';
  ip_address?: string;
  tailscale_ip?: string;
  // 'online' is written every 30s heartbeat. The transient values are written
  // by the agent right before it goes down so the operator can show a precise
  // state (with a timeout) instead of "off": 'restarting' (PC reboot),
  // 'updating' (self-update service swap), 'shutting_down' (deliberate power off).
  // A transient value paired with a stale last_heartbeat is how the tile decides
  // restarting-vs-off (see PCTile lifecycle derivation).
  status:
    | 'online'
    | 'offline'
    | 'error'
    | 'restarting' // PC reboot
    | 'restarting_agent' // agent service bounce (PC stays on)
    | 'updating'
    | 'shutting_down';
  last_heartbeat?: string;
  agent_version?: string;
  player_index?: number;
  is_dedicated_server?: boolean;
  should_update?: boolean;
  experimental?: boolean;
  discord_user_id?: string;
  wake_requested?: boolean;
  wake_requested_at?: string;
  // What this machine is. 'nuc' = a room support box on the same LAN with no
  // RealityAgent: a Wake-on-LAN target only, never heartbeats, nothing on :9090.
  // Filtered out of every query except the wake-target list. Absent on old rows
  // ⇒ treat as 'experience_pc'.
  device_kind?: 'experience_pc' | 'nuc';
  // Operator sync pause (sticky): no leeching, no seeding until resumed.
  // The OPERATOR is the only writer of these columns; the agent reads them
  // (plus POST /api/sync/pause|resume for immediate effect).
  sync_paused?: boolean;
  sync_paused_at?: string | null;
  sync_paused_by?: string | null;
  // Out of the room LINEUP (repair / onderzoek / schoonmaak / anders). There is
  // no boolean for this anywhere: an OPEN operations.pc_out_of_service row IS
  // the state, merged in here by the dashboard's fetch. null/absent = in the
  // lineup. Not a power or sync flag either — a parked PC keeps heartbeating
  // and keeps syncing, it just never gets offered a visitor.
  out_of_service?: PCOutOfService | null;
  // Autodiagnostics DB mirror (written by the agent). Used ONLY for the
  // offline/rebooting tile and the dead-agent fallback — live tiles read the
  // agent's /api/status diagnostics block instead.
  diag_state?: DiagState | null;
  diag_run_id?: string | null;
  diag_detail?: DiagDetail | null;
  diag_updated_at?: string | null;
  // Agent-written at boot: newest operations.agent_config.updated_at this PC
  // merged (null = booted with no override rows). The Config editor compares it
  // against applicable agent_config rows to show a "restart to apply" badge.
  config_applied_at?: string | null;
  created_at: string;
  updated_at: string;
}

// jsonb payload of infra.pc_instances.diag_detail.
export interface DiagDetail {
  failed?: string[];
  advisory?: string[];
  pending_hmd?: string[];
  enforcement?: string;
  failure_code?: string;
  override?: { by?: string; reason?: string; at?: string };
}

// ---------------------------------------------------------------------------
// Software version management — operations.room_software_targets (desired
// version per room per component) + operations.pc_software_status (reported
// installed version + running per PC per component). See migration
// 20260609140000_software_version_management.
// ---------------------------------------------------------------------------

// 'hmd_shell' = the headset shell (RealityAgent/hmdagent). Target only: the
// installed version is per headset (infra.hmd_devices.agent_version), and the
// PC agent pushes the APK to every older connected headset (api/hmdshell.go).
export type SoftwareComponent = 'agent' | 'ngr-convai' | 'hmd_shell';

export interface RoomSoftwareTarget {
  id: string;
  room_id: string;
  component: SoftwareComponent;
  desired_version: string | null; // null = unmanaged (don't reconcile)
  updated_at: string;
  updated_by?: string | null;
}

export interface PCSoftwareStatus {
  id: string;
  pc_instance_id: string;
  component: SoftwareComponent;
  current_version: string | null;
  running: boolean;
  last_seen: string;
}

// ---------------------------------------------------------------------------
// RealityOperator device version targeting — operations.operator_devices.
// Registry of Operator installs (Android/Electron/web). Unlike PCs, Operator
// devices are targeted PER-DEVICE (device_id is the stable anchor; room_id is
// reported metadata only). desired_version pins a device to a version; the app
// OTA-reconciles toward it. See migration 20260722140631_operator_device_version_targeting.
// ---------------------------------------------------------------------------

export type OperatorPlatform = 'android' | 'electron' | 'web';

export interface OperatorDevice {
  id: string;
  device_id: string;
  label: string | null;
  platform: OperatorPlatform;
  room_id: string | null; // reported metadata only — never resolves the version
  current_version: string | null;
  desired_version: string | null; // null = unmanaged (don't reconcile)
  last_seen: string;
  created_at: string;
  updated_at: string;
  updated_by?: string | null;
}

// ---------------------------------------------------------------------------
// PICO Business Streaming settings — operations.room_streaming_settings
// (desired settings per room) + operations.pc_streaming_settings (per-PC
// override, deep-merged on top of the room settings by the agent). The
// settings value is a PARTIAL %APPDATA%\Business Streaming\settings.json —
// only the keys present are enforced. See migration
// 20260702161050_agent_business_streaming_settings.
// ---------------------------------------------------------------------------

export type StreamingSettings = Record<string, any>;

export interface RoomStreamingSettings {
  id: string;
  room_id: string;
  settings: StreamingSettings;
  updated_at: string;
  updated_by?: string | null;
}

export interface PCStreamingSettings {
  id: string;
  pc_instance_id: string;
  settings: StreamingSettings;
  updated_at: string;
  updated_by?: string | null;
}

// ---------------------------------------------------------------------------
// Experience launch args — operations.experience_launch_args. One row per
// (experience, scope); scope is whichever single column of room_id /
// pc_instance_id / experience_release_id is set (all null = experience-wide
// default). args holds semantic values keyed by the arg `id` from the build's
// LaunchArgs.json manifest, plus an optional "_raw" string[] of verbatim
// tokens. The agent resolves at launch time in order: build defaults →
// experience default → room → release override → PC override → ad-hoc request
// args. See migration 20260707160923_agent_experience_launch_args.
// ---------------------------------------------------------------------------

export type LaunchArgsValues = Record<string, any>;

export interface ExperienceLaunchArgs {
  id: string;
  experience_id: string;
  room_id: string | null;
  pc_instance_id: string | null;
  experience_release_id: string | null;
  args: LaunchArgsValues;
  updated_at: string;
  updated_by?: string | null;
}

// The scope target of one experience_launch_args row (at most one field set;
// none set = the experience-wide default row).
export interface LaunchArgsScope {
  experienceId: string;
  roomId?: string;
  pcInstanceId?: string;
  experienceReleaseId?: string;
}

// ---------------------------------------------------------------------------
// Agent config overrides — operations.agent_config. Operator-editable
// config.yaml overrides, layered by scope: global (both scope columns null) →
// room → PC (later wins). config is a PARTIAL config.yaml-shaped object keyed by
// yaml section then key, e.g. { gpu_health: { miss_threshold: 9 }, time_sync:
// { enabled: false } }. Only keys present are enforced; the agent honors only
// registry-listed paths (see agentConfigSchema.ts) and applies them at boot —
// restart-to-apply, no hot-reload. See migration 20260710134212_agent_config_overrides.
// ---------------------------------------------------------------------------

// { section: { key: value } }
export type AgentConfigValues = Record<string, Record<string, any>>;

export interface AgentConfig {
  id: string;
  room_id: string | null;
  pc_instance_id: string | null;
  config: AgentConfigValues;
  updated_at: string;
  updated_by?: string | null;
}

// The scope target of one agent_config row. Neither field set = the global row;
// roomId set = room override; pcInstanceId set = per-PC override.
export interface AgentConfigScope {
  roomId?: string;
  pcInstanceId?: string;
}

// LaunchArgs.json manifest shipped inside a packaged experience build,
// served verbatim by the agent's GET /api/experiences/launch-args-schema.
export interface LaunchArgsManifest {
  manifestVersion: string;
  experience: string;
  args: LaunchArgSpec[];
}

export interface LaunchArgSpec {
  id: string;
  category?: string; // "session" args are agent-owned and hidden from the form
  label?: string;
  kind: 'switch' | 'value' | 'enum';
  default?: any;
  arg?: string; // switch: token emitted when on
  argTemplate?: string; // value: template with {value} placeholder
  valueType?: 'int' | 'float' | 'string';
  options?: { value: string; arg?: string; description?: string }[]; // enum
  description?: string;
  specialValues?: Record<string, string>;
}

export interface PCSyncStatus {
  id: string;
  pc_instance_id: string;
  configuration_release_id: string;
  // 'waiting' = no peer has wanted pieces yet; 'paused' = operator pause
  // acknowledged with all progress kept (swarm sync).
  sync_status: 'pending' | 'downloading' | 'downloaded' | 'installed' | 'failed' | 'waiting' | 'paused';
  bytes_downloaded: number;
  total_bytes?: number;
  download_percentage: number;
  files_downloaded: number;
  total_files?: number;
  // 'swarm' = piece-level peer swarm (a leecher has N sources, so there is no
  // single source PC; live peer detail is in sync_detail / the agent status).
  download_source?: 'primary' | 's3' | 'swarm';
  // Swarm health written on the agent's 10s progress throttle.
  sync_detail?: {
    peers?: number;
    upstream_peers?: number;
    seeding_to?: number;
    pieces_verified?: number;
    piece_count?: number;
  } | null;
  sync_started_at?: string;
  sync_completed_at?: string;
  last_attempt_at?: string;
  error_message?: string;
  attempt_count: number;
  created_at: string;
  updated_at: string;
}

export interface Language {
  id: number;
  name: string;
  short_name: string;
  created_at: string;
}

export interface ExperienceLanguageSupport {
  id: string;
  experience_id: string;
  elevenlabs_agent_id?: string;
  priority: number;
  created_at: string;
  // Merged data from join
  language?: Language;
}

export interface Experience {
  id: string;
  name: string;
  project_name: string;
  description?: string;
  version?: string;
  is_multiplayer?: boolean;
  use_vc?: boolean;
  min_players?: number;
  max_players?: number;
  instruction_image_path?: string;
  created_at: string;
  // Language support
  supported_languages?: ExperienceLanguageSupport[];
}

export interface ConfigurationRelease {
  id: string;
  configuration_id: string;
  experience_id: string;
  phase: string;
  experience_release_id: string;
  created_at: string;
  // Merged data from related tables
  experiences?: Experience;
  experience_release?: ExperienceRelease;
}

export interface ExperienceRelease {
  id: string;
  version: string;
  path: string;
  experience_id: string;
  description?: string;
  build_type?: 'client' | 'server';
  created_at: string;
  avg_loading_time_ms?: number;
}

export interface Configuration {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

// Notification from RealityAgent
export interface Notification {
  id: string;
  type: 'info' | 'warning' | 'error';
  text: string;
  start_time: string;
  duration?: number; // Duration in seconds, undefined means manual stop required
  beep?: 'none' | 'once' | 'repeat'; // Audio alert behavior
  // Policy-driven decoration (agent resolves operations.incident_policies and
  // stamps these on the notification, so the operator renders a stable generic
  // contract without knowing the policy schema). Absent on older agents.
  category?: string; // issue category key (e.g. experience_no_vr)
  actions?: string[]; // e.g. ['repair','dismiss'] — which controls to offer
  confirm_dismiss?: boolean; // prompt before dismissing
  confirm_reboot?: boolean; // prompt before the reboot escalation
  // Machine-readable context for specially rendered notifications. The
  // auto-repair countdown uses kind/phase/started_at/estimate_s: remaining =
  // estimate_s - (now - started_at). Absent on older agents.
  meta?: {
    kind?: string;
    phase?: string; // rebooting | testing | success | hardware_needed | hmd_needed | stream_failed
    started_at?: string; // RFC3339
    estimate_s?: number;
    [k: string]: unknown;
  };
}

// PC Status from RealityAgent API
export interface HmdBoundaryStatus {
  state: 'unknown' | 'ok' | 'lost' | 'rescanning' | 'failed';
  serial?: string;
  since?: string;
  rescans: number;
  last_rescan_at?: string;
  episodes: number;
  last_restored_by?: string;
  auto_rescan: boolean;
}

// Headset IPD lock (agent api/hmdipd.go): the agent re-sends `mm` to the
// paired headset every `interval_seconds` unless `manual` (kid mode, set from
// the dispatch pop-up) suspends it for the current run. The headset cannot
// report its IPD, so last_set_at/last_ok are the only feedback.
export interface HmdIpdStatus {
  enforce: boolean;
  mm: number;
  interval_seconds: number;
  manual: boolean;
  manual_since?: string;
  manual_by?: string;
  serial?: string;
  last_set_at?: string;
  last_ok?: boolean;
  last_error?: string;
  sets: number;
}

export interface PCAgentStatus {
  pc_name: string;
  pc_instance_id: string;
  network_id: string;
  role: string;
  is_dedicated_server?: boolean;
  last_heartbeat: string;
  uptime_seconds: number;
  mp_connected?: boolean;
  mp_server_ip?: string;
  // Headset shadow sender (agent api/mpshadow.go): relayed = the multiplayer
  // server is currently showing this seat via the headset pose because the
  // game itself is silent.
  mp_shadow?: {
    enabled: boolean; active: boolean; relayed: boolean; skipped?: string;
    floor_source?: 'headset' | 'knob'; floor_m?: number;
  };
  // Headset fence watcher (agent api/hmdboundary.go, shell >= 1.0.12): the
  // guardian's "fence is drawn" flag. lost = fence gone while worn,
  // rescanning = the agent forced a re-scan, failed = still gone afterwards
  // (the agent also raises the hmd_boundary notification).
  hmd_boundary?: HmdBoundaryStatus;
  // Headset IPD lock + kid mode (absent = agent too old for it).
  hmd_ipd?: HmdIpdStatus;
  danger_zone?: boolean;
  danger_zone_reason?: string;
  notifications?: Notification[];
  current_experience?: {
    id: string;
    name: string;
    version: string;
    pid: number;
    start_time: string;
    status: string;
    is_loading?: boolean;
    is_server?: boolean;
    // Present once the build has reported a level via /api/experiences/level.
    current_level?: number;
    scene_name?: string;
    level_start_time?: string;
    // Run correlation (agent ≥ 1.2.189) — ties this process to its
    // experience_runs / experience_crashes rows. Used by the Test Lab soak.
    run_id?: string;
  };
  pico?: {
    connected: boolean;
    battery?: number;
    // Inferred by the agent from the battery level trend (the PICO SDK has no
    // charging flag); absent = unknown, true = charging, false = discharging.
    charging?: boolean;
    serial?: string;
    connectionStatus?: number;
    connectionStatusText?: string;
    error?: string;
    lastUpdated: string;
  };
  convai?: {
    running: boolean;
    installed_version?: string;
    frozen?: boolean;
  };
  headset?: HeadsetStatus;
  server_time?: string;
  // Autodiagnostics (boot self-test) block — absent on old agents, in which
  // case the tile renders exactly the pre-diagnostics behavior.
  diagnostics?: DiagnosticsStatus;
  // Build-sync availability block (swarm distribution) — absent on old
  // agents, in which case the tile renders exactly today's behavior.
  sync?: SyncStatusBlock;
  // Crash recovery block — absent on old agents, which the tile must read as
  // "no crash on record" (undefined = healthy).
  crash_recovery?: CrashRecovery;
}

// ---------------------------------------------------------------------------
// Crash recovery — mirrors the agent's always-present /api/status
// `crash_recovery` block (RealityAgent api/crash_recovery.go). Without it a
// crash-and-relaunch and a visitor who finished look identical from the
// operator's side: the experience simply disappears. Empty string / 0 / null
// mean "no crash on record". Cleared agent-side by a clean exit, by the
// completion callback and by a fresh operator launch.
// ---------------------------------------------------------------------------

export interface CrashRecovery {
  /** A recovery relaunch is in flight: the visitor has NOT left this PC. Also
   *  raised by the freeze ladder, which shares the relaunch guard. */
  restarting: boolean;
  /** Crashes counted inside window_seconds; 0 = no crash on record. */
  crashes_in_window: number;
  /** The agent's sliding loop-guard window (config CrashRecovery.RestartWindowSec). */
  window_seconds: number;
  /** Loop guard tripped (or auto-restart off): the agent stopped relaunching.
   *  Mirrors the persistent crash_recovery_failed banner. */
  gave_up: boolean;
  /** Exit code of the last crash; 0 when there is no crash on record. */
  last_exit_code: number;
  last_crash_at: string | null;
  /** The run that crashed; recovery RESUMES the same run_id. '' = none. */
  experience_id: string;
  experience_name: string;
  run_id: string;
}

// ---------------------------------------------------------------------------
// Build-sync availability — mirrors the agent's /api/status `sync` block and
// the POST /api/sync/pause|resume endpoints (swarm piece distribution).
// ---------------------------------------------------------------------------

export interface SyncStatusBlock {
  // Leeching or seeding right now.
  busy: boolean;
  // THE sync side of the launch gate (combined with diagnostics.can_launch).
  can_launch: boolean;
  // report_only never blocks launches; block returns 409 sync_in_progress.
  gate: 'report_only' | 'block';
  paused: { active: boolean; by?: string; at?: string };
  deferred: 'experience_running' | null;
  pulling: {
    active: boolean;
    experience?: string;
    version?: string;
    release_id?: string;
    source?: 'swarm' | 's3';
    pct?: number;
    bytes_done?: number;
    bytes_total?: number;
    pieces_done?: number;
    pieces_total?: number;
    peers?: number;
    upstream_peers?: number;
  };
  serving: { active: boolean; connections: number };
}

// ---------------------------------------------------------------------------
// Autodiagnostics — the agent's boot self-test cycle. Mirrors the agent's
// /api/status `diagnostics` block and the /api/diagnostics/* endpoints.
// ---------------------------------------------------------------------------

// Agent diagnostics state machine. 'off'|'idle'|'aborted'|missing ⇒ tiles
// behave exactly as before diagnostics existed.
export type DiagState =
  | 'off'
  | 'idle'
  | 'testing'
  | 'fixing'
  | 'rebooting'
  | 'passed'
  | 'degraded_pass'
  | 'malfunctioning'
  | 'overridden'
  | 'aborted';

// One test row as reported in diagnostics.tests[] and the run report.
export interface DiagnosticsTestView {
  id: string;
  status: string;
  severity: string; // 'blocking' | 'advisory'
  failure_code: string;
  message: string;
  duration_ms: number;
}

export interface DiagnosticsOverride {
  active: boolean;
  by?: string;
  reason?: string;
  at?: string;
}

export interface DiagnosticsLastResult {
  status: string;
  finished_at: string;
  blocking_failed: string[];
  advisory_failed: string[];
  skipped: string[];
}

// The diagnostics block on PCAgentStatus. run_id and below are present only
// while/after a run.
export interface DiagnosticsStatus {
  state: DiagState;
  enforcement: 'report_only' | 'block';
  reboots_24h: number;
  // THE single field launch gating reads (undefined block ⇒ allowed).
  can_launch: boolean;
  override?: DiagnosticsOverride;
  run_id?: string;
  trigger?: string;
  started_at?: string;
  deadline_at?: string;
  progress?: { done: number; total: number; current_test: string };
  current_fix?: { fix_id: string; test_id: string; attempt: number; max: number };
  pending_hmd_tests?: string[];
  tests?: DiagnosticsTestView[];
  last_result?: DiagnosticsLastResult;
}

// Full report from GET /api/diagnostics/run (drawer/modal view).
export interface DiagnosticsReport {
  state: DiagState;
  enforcement: 'report_only' | 'block';
  run_id?: string;
  trigger?: string;
  tests: DiagnosticsTestView[];
  pending_hmd?: string[];
  reboots_24h: number;
  last_result?: DiagnosticsLastResult;
  override?: DiagnosticsOverride;
}

// Headset Status from RealityAgent GHub integration
export interface HeadsetStatus {
  connected: boolean;
  device_id?: string;
  device_name?: string;
  device_model?: string;
  battery_level?: number;
  charging?: boolean;
  mileage?: number;
  last_updated?: string;
  error?: string;
}

// Combined PC info for dashboard
export interface PCTileData {
  instance: PCInstance;
  agentStatus?: PCAgentStatus;
  isOnline: boolean;
  currentExperience?: {
    id: string;
    name: string;
    version: string;
    pid: number;
  };
  volume?: number;
}

// Multiplayer session status — UI uses all values, DB uses all except 'waiting_for_server'
export type MultiplayerSessionStatus =
  | 'starting_server'
  | 'waiting_for_server'  // UI-only, DB stays at 'starting_server' until server_ready
  | 'server_ready'
  | 'launching_clients'
  | 'active'
  | 'failed'
  | 'stopping'
  | 'completed';

// DB-compatible status values (subset without 'waiting_for_server')
export type MultiplayerSessionDbStatus = Exclude<MultiplayerSessionStatus, 'waiting_for_server'>;

export interface MultiplayerSession {
  id?: string;
  experienceId: string;
  experienceName: string;
  roomId?: string;
  serverPcId: string;
  serverIp: string;
  serverId?: string; // Dedicated server instance ID from RealityAgent server manager
  clientPcIds: string[];
  status: MultiplayerSessionStatus;
  error?: string;
  startedAt: number;
  serverReadyAt?: number;
}

// Centralized logging
export interface LogEntry {
  id: number;
  created_at: string;
  pc_instance_id: string | null;
  network_id: string | null;
  room_id: string | null;
  app_name: string;
  category: string | null;
  level: string;
  message: string;
  metadata: Record<string, any> | null;
  source_file: string | null;
  timestamp: string;
}

export interface LogFilter {
  pcInstanceId?: string;
  networkId?: string;
  roomId?: string;
  appName?: string;
  category?: string;
  level?: string;
  search?: string;
  startTime?: string;
  endTime?: string;
  limit?: number;
  offset?: number;
}

// Operator authentication
export interface Operator {
  id: string;
  role: string;
  name?: string | null;
  department?: string;
  permissions?: Record<string, any>;
  is_active: boolean;
  created_at: string;
  last_login?: string;
  location_id?: string;
  operator_code?: string;
}

// ---------------------------------------------------------------------------
// Access Control (admin page) — backed by operations.roles / operations.permissions
// and the operations.admin_* RPCs (see migration 20260609130000_operator_access_admin).
// ---------------------------------------------------------------------------

// A role from operations.roles. `key` is the stable identifier stored on
// operators.role; system roles (admin) cannot be renamed or deleted.
export interface Role {
  key: string;
  name: string;
  description?: string | null;
  is_system: boolean;
  sort_order: number;
  created_at: string;
}

// A permission from the operations.permissions catalog — the set of "what is
// possible", grouped by category for display.
export interface PermissionDef {
  key: string;
  label: string;
  description?: string | null;
  category: string;
  sort_order: number;
}

// An operator as returned by operations.admin_list_operators (richer than the
// login-time Operator: includes operator_code/email so admins can identify it).
export interface AdminOperator {
  id: string;
  operator_code: string | null;
  name: string | null;
  role: string;
  department?: string | null;
  email?: string | null;
  is_active: boolean;
  is_unrestricted: boolean;
  location_ids: string[];
  last_login?: string | null;
  created_at: string;
  // Per-operator additive grants, e.g. { "restart_pc": true }.
  permissions: Record<string, boolean>;
}

// infra.hmd_devices — PICO headset registry. Rows appear when a headset announces
// itself to a room primary's agent. Operators own `label` and
// `paired_pc_instance_id` (the DESIRED PC); agents write the observed_* and bs_*
// observation columns. No DELETE: retire with active=false.
export interface HmdDevice {
  id: string;
  wifi_mac: string;
  label: string | null;
  serial: string | null;
  network_id: string | null;
  paired_pc_instance_id: string | null;
  observed_pc_instance_id?: string | null;
  observed_at?: string | null;
  bs_ip?: string | null;
  bs_ip_checked_at?: string | null;
  bs_error?: string | null;
  last_announced_at?: string | null;
  agent_version?: string | null;
  discovered_by: string;
  active: boolean;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

// ---------------------------------------------------------------------------
// Room plan for the operator's playspace map — experiences.playspaces (active
// boundary + obstacles) plus the room-wide playspace_zones / playspace_walls
// rows RealityStudio draws (scene_name '__room__'). Plan metres; the headset
// pose maps onto it as plan x = -local.x, plan y = -local.z (measured 2026-08-24).
// ---------------------------------------------------------------------------

export interface PlanPoint {
  x: number;
  y: number;
}

export interface RoomPlayspace {
  id: string | null;
  boundary: PlanPoint[];
  obstacles: PlanPoint[][];
  zones: Record<string, PlanPoint[]>;
  walls: Record<string, PlanPoint[]>;
}
